import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/hooks/query-keys';
import { useRealtime } from '@/lib/realtime/core/useRealtime';
import { useOrganizationStore } from '@/store/organization.store';
import type {
  PaginatedResponse,
  WorkflowDetail,
  WorkflowListItem,
  WorkflowRunDetail,
} from '@scrambl/shared';
import { SocketEvent } from '@scrambl/shared';

interface UseWorkflowsRealtimeOptions {
  enabled?: boolean;
  selectedWorkflowId?: string | null;
  selectedRunId?: string | null;
  suspendDetailUpdates?: boolean;
}

type WorkflowPayload = Partial<WorkflowDetail> & {
  id?: string;
  organizationId?: string;
  stats?: WorkflowListItem['stats'];
};

type WorkflowRunPayload = Partial<WorkflowRunDetail> & {
  id?: string;
  workflowId?: string;
  organizationId?: string;
};

const workflowRunsPrefix = (workflowId: string) =>
  [...queryKeys.workflows.detail(workflowId), 'runs'] as const;

export function useWorkflowsRealtime({
  enabled = true,
  selectedWorkflowId,
  selectedRunId,
  suspendDetailUpdates = false,
}: UseWorkflowsRealtimeOptions = {}) {
  const { currentOrganization } = useOrganizationStore();
  const orgId = currentOrganization?.id || null;
  const queryClient = useQueryClient();
  const lastUpdateRef = useRef<Map<string, number>>(new Map());
  const { socket, isReady, joinRoom, leaveRoom } = useRealtime({
    suppressLogging: true,
  });

  const belongsToOrganization = useCallback(
    (payload: { organizationId?: string }) =>
      !orgId || !payload.organizationId || payload.organizationId === orgId,
    [orgId],
  );

  const updateWorkflowInLists = useCallback(
    (workflow: WorkflowPayload & { id: string }) => {
      const listQueries = queryClient.getQueriesData<
        PaginatedResponse<WorkflowListItem> | WorkflowListItem[]
      >({
        queryKey: queryKeys.workflows.lists(),
      });

      listQueries.forEach(([queryKey, oldData]) => {
        if (!oldData) return;

        if (
          typeof oldData === 'object' &&
          'data' in oldData &&
          Array.isArray(oldData.data)
        ) {
          const index = oldData.data.findIndex(item => item.id === workflow.id);
          if (index === -1) return;

          const next = [...oldData.data];
          next[index] = {
            ...next[index],
            ...workflow,
            stats: workflow.stats ?? next[index].stats,
          } as WorkflowListItem;
          queryClient.setQueryData(queryKey, { ...oldData, data: next });
          return;
        }

        if (Array.isArray(oldData)) {
          const index = oldData.findIndex(item => item.id === workflow.id);
          if (index === -1) return;

          const next = [...oldData];
          next[index] = {
            ...next[index],
            ...workflow,
            stats: workflow.stats ?? next[index].stats,
          } as WorkflowListItem;
          queryClient.setQueryData(queryKey, next);
        }
      });
    },
    [queryClient],
  );

  const removeWorkflowFromLists = useCallback(
    (id: string) => {
      queryClient.removeQueries({ queryKey: queryKeys.workflows.detail(id) });

      const listQueries = queryClient.getQueriesData<
        PaginatedResponse<WorkflowListItem> | WorkflowListItem[]
      >({
        queryKey: queryKeys.workflows.lists(),
      });

      listQueries.forEach(([queryKey, oldData]) => {
        if (!oldData) return;

        if (
          typeof oldData === 'object' &&
          'data' in oldData &&
          Array.isArray(oldData.data)
        ) {
          const next = oldData.data.filter(item => item.id !== id);
          if (next.length === oldData.data.length) return;
          queryClient.setQueryData(queryKey, { ...oldData, data: next });
          return;
        }

        if (Array.isArray(oldData)) {
          const next = oldData.filter(item => item.id !== id);
          if (next.length === oldData.length) return;
          queryClient.setQueryData(queryKey, next);
        }
      });
    },
    [queryClient],
  );

  const updateRunInLists = useCallback(
    (run: WorkflowRunPayload & { id: string; workflowId: string }) => {
      const runQueries = queryClient.getQueriesData<
        PaginatedResponse<WorkflowRunDetail> | WorkflowRunDetail[]
      >({
        queryKey: workflowRunsPrefix(run.workflowId),
      });

      runQueries.forEach(([queryKey, oldData]) => {
        if (!oldData) return;

        if (
          typeof oldData === 'object' &&
          'data' in oldData &&
          Array.isArray(oldData.data)
        ) {
          const existingIndex = oldData.data.findIndex(item => item.id === run.id);
          const next =
            existingIndex === -1
              ? [run as WorkflowRunDetail, ...oldData.data]
              : oldData.data.map(item =>
                  item.id === run.id
                    ? ({ ...item, ...run } as WorkflowRunDetail)
                    : item,
                );
          queryClient.setQueryData(queryKey, { ...oldData, data: next });
          return;
        }

        if (Array.isArray(oldData)) {
          const existingIndex = oldData.findIndex(item => item.id === run.id);
          const next =
            existingIndex === -1
              ? [run as WorkflowRunDetail, ...oldData]
              : oldData.map(item =>
                  item.id === run.id
                    ? ({ ...item, ...run } as WorkflowRunDetail)
                    : item,
                );
          queryClient.setQueryData(queryKey, next);
        }
      });
    },
    [queryClient],
  );

  const onWorkflowCreated = useCallback(
    (data: Record<string, unknown>) => {
      const workflow = data as WorkflowPayload;
      if (!workflow.id || !belongsToOrganization(workflow)) return;

      if (
        !(suspendDetailUpdates && workflow.id === selectedWorkflowId) &&
        workflow.definition
      ) {
        queryClient.setQueryData(
          queryKeys.workflows.detail(workflow.id),
          workflow as WorkflowDetail,
        );
      }

      void queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.lists(),
        refetchType: 'active',
      });
    },
    [
      belongsToOrganization,
      queryClient,
      selectedWorkflowId,
      suspendDetailUpdates,
    ],
  );

  const onWorkflowUpdated = useCallback(
    (data: Record<string, unknown>) => {
      const workflow = data as WorkflowPayload;
      if (!workflow.id || !belongsToOrganization(workflow)) return;

      const now = Date.now();
      const lastUpdate = lastUpdateRef.current.get(workflow.id);
      if (lastUpdate && now - lastUpdate < 200) return;
      lastUpdateRef.current.set(workflow.id, now);

      updateWorkflowInLists(workflow as WorkflowPayload & { id: string });

      const shouldUpdateDetail =
        !(suspendDetailUpdates && workflow.id === selectedWorkflowId) &&
        Boolean(workflow.definition);

      if (shouldUpdateDetail) {
        queryClient.setQueryData(
          queryKeys.workflows.detail(workflow.id),
          (old: WorkflowDetail | undefined) =>
            old ? ({ ...old, ...workflow } as WorkflowDetail) : workflow,
        );
      }

      void queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.lists(),
        refetchType: 'active',
      });

      if (!suspendDetailUpdates || workflow.id !== selectedWorkflowId) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.workflows.detail(workflow.id),
          refetchType: 'active',
        });
      }
    },
    [
      belongsToOrganization,
      queryClient,
      selectedWorkflowId,
      suspendDetailUpdates,
      updateWorkflowInLists,
    ],
  );

  const onWorkflowDeleted = useCallback(
    (data: Record<string, unknown>) => {
      const workflow = data as { id?: string; organizationId?: string };
      if (!workflow.id || !belongsToOrganization(workflow)) return;

      removeWorkflowFromLists(workflow.id);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.lists(),
        refetchType: 'active',
      });
    },
    [belongsToOrganization, queryClient, removeWorkflowFromLists],
  );

  const onWorkflowRunChanged = useCallback(
    (data: Record<string, unknown>) => {
      const run = data as WorkflowRunPayload;
      if (!run.id || !run.workflowId || !belongsToOrganization(run)) return;

      updateRunInLists(
        run as WorkflowRunPayload & { id: string; workflowId: string },
      );

      if (run.id === selectedRunId) {
        queryClient.setQueryData(
          queryKeys.workflows.run(run.workflowId, run.id),
          (old: WorkflowRunDetail | null | undefined) =>
            old ? ({ ...old, ...run } as WorkflowRunDetail) : run,
        );
      }

      void queryClient.invalidateQueries({
        queryKey: workflowRunsPrefix(run.workflowId),
        refetchType: 'active',
      });
    },
    [belongsToOrganization, queryClient, selectedRunId, updateRunInLists],
  );

  useEffect(() => {
    if (!enabled || !socket || !isReady || !orgId) {
      return;
    }

    joinRoom('organization', orgId);

    socket.on(SocketEvent.WORKFLOW_CREATED, onWorkflowCreated);
    socket.on(SocketEvent.WORKFLOW_UPDATED, onWorkflowUpdated);
    socket.on(SocketEvent.WORKFLOW_DELETED, onWorkflowDeleted);
    socket.on(SocketEvent.WORKFLOW_RUN_CREATED, onWorkflowRunChanged);
    socket.on(SocketEvent.WORKFLOW_RUN_UPDATED, onWorkflowRunChanged);

    return () => {
      socket.off(SocketEvent.WORKFLOW_CREATED, onWorkflowCreated);
      socket.off(SocketEvent.WORKFLOW_UPDATED, onWorkflowUpdated);
      socket.off(SocketEvent.WORKFLOW_DELETED, onWorkflowDeleted);
      socket.off(SocketEvent.WORKFLOW_RUN_CREATED, onWorkflowRunChanged);
      socket.off(SocketEvent.WORKFLOW_RUN_UPDATED, onWorkflowRunChanged);
      leaveRoom('organization', orgId);
    };
  }, [
    enabled,
    isReady,
    joinRoom,
    leaveRoom,
    onWorkflowCreated,
    onWorkflowDeleted,
    onWorkflowRunChanged,
    onWorkflowUpdated,
    orgId,
    socket,
  ]);
}
